import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { STEP_SEARCH_PARAM, useSelectStep } from '~/routes/workflowStepParam';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    DEFAULT_WORKFLOW_STEP,
    STEP_REQUIREMENT_REASON_ID,
    StepRequirement,
    WORKFLOW_STEPS,
    WorkflowStep,
    WorkflowStepId,
    workflowStepById,
} from './workflow-steps';

export interface StepAvailability {
    disabled: boolean;
    disabledReasonId?: string;
}

const AVAILABLE: StepAvailability = { disabled: false };

export interface WorkflowStepState {
    /** Always a step: an unnamed, unknown or unreachable one resolves to the default. */
    currentStep: WorkflowStep;
    currentStepIndex: number;
    availability: Record<string, StepAvailability>;
    /** Moves to a step by writing the search parameter. */
    selectStep: (id: WorkflowStepId) => void;
}

/**
 * Single source of truth for workflow step state: which step the URL asks for,
 * and which steps the loaded data can support.
 *
 * The `step` parameter is a **request, not a location**. A step whose
 * requirements are not met yet resolves to the default and the parameter is
 * left alone, so the moment the data arrives the user is where they asked to
 * be. That is why nothing here redirects: the previous version navigated away
 * from an unavailable step, which raced every load that was about to make it
 * available.
 */
export default function useWorkflowSteps(): WorkflowStepState {
    const [searchParams] = useSearchParams();
    const selectStep = useSelectStep();
    const primaryImage = useSelectedPrimaryImage();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const patient = useSelectedPatient();

    const availability = useMemo(() => {
        const hasImage = !!primaryImage;
        const fulfilled: Record<StepRequirement, boolean> = {
            image: hasImage,
            secondaryImages: hasImage && !!patient && patient.images.length > 1,
            structureSet: !!currentStructureSet,
        };

        const result: Record<string, StepAvailability> = {};
        for (const step of WORKFLOW_STEPS) {
            const unmet = step.requires?.find((req) => !fulfilled[req]);
            result[step.id] = unmet
                ? {
                      disabled: true,
                      disabledReasonId: STEP_REQUIREMENT_REASON_ID[unmet],
                  }
                : AVAILABLE;
        }
        return result;
    }, [primaryImage, patient, currentStructureSet]);

    const currentStep = useMemo(() => {
        const requested = workflowStepById(searchParams.get(STEP_SEARCH_PARAM));
        return requested && !availability[requested.id]?.disabled
            ? requested
            : DEFAULT_WORKFLOW_STEP;
    }, [searchParams, availability]);

    return {
        currentStep,
        currentStepIndex: WORKFLOW_STEPS.indexOf(currentStep),
        availability,
        selectStep,
    };
}
